import React, { FC, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import type { AddPrintingMethodPanelProps } from '../common/types';
import type { addPrintingMethodPanel_PrintTypesListQuery } from './__generated__/addPrintingMethodPanel_PrintTypesListQuery.graphql';
import {
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    PanelItem,
    ItemDot,
    ItemName,
    ItemActionSuccess,
    PanelItemSpaced,
    ItemNameAction,
    EmptyState
} from '../common/styled';

const AddPrintingMethodPanelContent: FC<AddPrintingMethodPanelProps> = ({ productId, existingMethodIds, onBack, onCreateNew, onSelectExisting }) => {
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addPrintingMethodPanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addPrintingMethodPanel_PrintTypesListQuery>(
        graphql`
            query addPrintingMethodPanel_PrintTypesListQuery {
                printTypes(first: 200) {
                    edges {
                        node {
                            id
                            printTypeID
                            name
                        }
                    }
                }
            }
        `,
        {}
    );

    const allMethods = React.useMemo(() => {
        const edges = data?.printTypes?.edges || [];
        return edges.map((edge: { node: { id: string; printTypeID: number; name: string } }) => ({
            id: edge.node.id,
            numericId: String(edge.node.printTypeID),
            name: edge.node.name
        }));
    }, [data]);

    const getNextPrintMethodNumber = () => {
        const numbers = allMethods.map((printMethod: { name: string }) => {
            const match = printMethod.name.match(/^Printing Method (\d+)$/);
            return match ? parseInt(match[1]) : 0;
        });
        return Math.max(0, ...numbers) + 1;
    };

    const availableMethods = allMethods.filter((printMethod: { id: string }) => !existingMethodIds.includes(printMethod.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add printing method', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableMethods.length === 0 && <EmptyState>{T._('All printing methods are already assigned', 'Admin')}</EmptyState>}
                {availableMethods.map((printMethod: { id: string; numericId: string; name: string }) => (
                    <PanelItem key={printMethod.id} $clickable onClick={() => onSelectExisting(printMethod.id, printMethod.name)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{printMethod.name}</ItemName>
                        <ItemActionSuccess>{T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced $clickable onClick={() => onCreateNew(getNextPrintMethodNumber())}>
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddPrintingMethodPanel: FC<AddPrintingMethodPanelProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add printing method', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddPrintingMethodPanelContent {...props} />
        </Suspense>
    );
};
